import React from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useUpdateNodeConfig } from '../../hooks/useNode';
import { useInviteMember } from '../../hooks/useDirectory';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { 
  Users, 
  Shield, 
  Link as LinkIcon, 
  Plus, 
  Trash2, 
  Edit2,
  Copy,
  Check,
  Mail,
  Send
} from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '../../lib/utils';
import { Capability, RoleDefinition } from '../../types';

import { TIER_LIMITS } from '../../constants';
import { ProgressBar } from '../../components/ui/ProgressBar';

const ALL_CAPABILITIES: { value: Capability; label: string }[] = [
  { value: 'create_announcement', label: 'Create Announcements' },
  { value: 'send_email_blast', label: 'Email Blasts' },
  { value: 'manage_members', label: 'Manage Members' },
  { value: 'view_ledger', label: 'View Ledger' },
  { value: 'configure_fees', label: 'Configure Fees' },
  { value: 'verify_requirement', label: 'Verify Compliance' },
  { value: 'create_event', label: 'Create Events' },
  { value: 'create_poll', label: 'Create Polls' },
  { value: 'manage_vault', label: 'Manage Vault' },
  { value: 'create_newsletter', label: 'Create Newsletters' },
  { value: 'manage_message_board', label: 'Manage Board' },
  { value: 'manage_referrals', label: 'Manage Referrals' },
  { value: 'manage_elections', label: 'Manage Elections' },
  { value: 'cast_vote', label: 'Cast Vote' },
  { value: 'create_goal', label: 'Create Goal' },
  { value: 'create_requirement', label: 'Create Requirement' },
  { value: 'view_analytics', label: 'View Analytics' },
  { value: 'view_newsletter', label: 'View Newsletters' },
  { value: 'view_chat', label: 'View Chat' },
  { value: 'view_calendar', label: 'View Calendar' },
  { value: 'view_requirements', label: 'View Requirements' },
  { value: 'view_goals', label: 'View Goals' },
  { value: 'view_discussions', label: 'View Discussions' },
  { value: 'view_polls', label: 'View Polls' },
  { value: 'view_directory', label: 'View Directory' },
  { value: 'view_leadership', label: 'View Leadership' },
  { value: 'view_ledger_admin', label: 'Admin Ledger' },
  { value: 'view_personal_finances', label: 'Personal Ledger' },
  { value: 'view_elections', label: 'View Elections' },
  { value: 'define_roles', label: 'Define Roles' },
  { value: 'create_sub_orgs', label: 'Create Sub-orgs' },
  { value: 'view_suborgs', label: 'View Sub-orgs' },
  { value: 'edit_settings', label: 'Edit Settings' },
  { value: 'view_support', label: 'View Support' },
];

export const MemberSettingsPage = () => {
  const perms = usePermissions();
  const { node, lineage, getTerm } = useTenant();
  const updateMutation = useUpdateNodeConfig(node?.id || '');
  
  const memberTerm = getTerm('member');
  const canManageRoles = perms.isAdmin || perms.can('ROLE_MANAGE');
  const canInviteMembers = perms.isAdmin || perms.can('MEMBER_INVITE');
  
  const tierLimits = TIER_LIMITS[node?.tier || 'LOCAL'];
  const memberCount = node?.memberCount || 0;
  const memberLimit = tierLimits.maxMembers;
  const memberPercentage = Math.min((memberCount / memberLimit) * 100, 100);
  const isRosterFull = memberCount >= memberLimit;

  // Local + Inherited roles
  const [localRoleDefinitions, setLocalRoleDefinitions] = React.useState<RoleDefinition[]>(node?.config?.roleDefinitions || []);

  const [prevRoleDefinitions, setPrevRoleDefinitions] = React.useState<RoleDefinition[] | null>(null);
  if (node?.config?.roleDefinitions && prevRoleDefinitions !== node.config.roleDefinitions) {
    setPrevRoleDefinitions(node.config.roleDefinitions);
    setLocalRoleDefinitions(node.config.roleDefinitions);
  }
  
  const inheritedRoles = React.useMemo(() => {
    const roles: RoleDefinition[] = [];
    lineage.forEach(p => {
      (p.config?.roleDefinitions || []).forEach(r => {
        roles.push({ ...r, inherited: true, isLocked: true });
      });
    });
    return roles;
  }, [lineage]);

  const roleDefinitions = [...inheritedRoles, ...localRoleDefinitions];
  const [newRoleName, setNewRoleName] = React.useState('');
  const [newRoleCaps, setNewRoleCaps] = React.useState<Capability[]>([]);
  const [editingRole, setEditingRole] = React.useState<string | null>(null);
  const [editRoleName, setEditRoleName] = React.useState('');
  const [editRoleCaps, setEditRoleCaps] = React.useState<Capability[]>([]);
  const [inviteRole, setInviteRole] = React.useState('MEMBER');
  const [copied, setCopied] = React.useState(false);
  const [inviteEmail, setInviteEmail] = React.useState('');
  const inviteMutation = useInviteMember(node?.id || '');

  const handleSendInvite = () => {
    if (!inviteEmail) return;
    inviteMutation.mutate({
      email: inviteEmail,
      role: inviteRole
    }, {
      onSuccess: () => {
        setInviteEmail('');
      }
    });
  };

  const handleAddRole = () => {
    if (!newRoleName) return;
    const newRole: RoleDefinition = {
      name: newRoleName.toUpperCase().replace(/\s+/g, '_'),
      capabilities: newRoleCaps,
      isLocked: false,
      inherited: false
    };
    
    const updated = [...localRoleDefinitions, newRole];
    setLocalRoleDefinitions(updated);
    setNewRoleName('');
    setNewRoleCaps([]);
    
    updateMutation.mutate({ roleDefinitions: updated }, {
      onSuccess: () => toast.success('Role added'),
      onError: () => toast.error('Failed to add role')
    });
  };

  const handleToggleCap = (cap: Capability) => {
    setNewRoleCaps(prev => 
      prev.includes(cap) ? prev.filter(c => c !== cap) : [...prev, cap]
    );
  };

  const handleEditRole = (role: RoleDefinition) => {
    setEditingRole(role.name);
    setEditRoleName(role.name);
    setEditRoleCaps(role.capabilities);
  };

  const handleToggleEditCap = (cap: Capability) => {
    setEditRoleCaps(prev => 
      prev.includes(cap) ? prev.filter(c => c !== cap) : [...prev, cap]
    );
  };

  const handleUpdateRole = () => {
    if (!editingRole || !editRoleName) return;
    
    const updated = localRoleDefinitions.map(r => {
      if (r.name === editingRole) {
        return {
          ...r,
          name: editRoleName.toUpperCase().replace(/\s+/g, '_'),
          capabilities: editRoleCaps
        };
      }
      return r;
    });
    
    setLocalRoleDefinitions(updated);
    setEditingRole(null);
    
    updateMutation.mutate({ roleDefinitions: updated }, {
      onSuccess: () => toast.success('Role updated'),
      onError: () => toast.error('Failed to update role')
    });
  };

  const handleRemoveRole = (name: string) => {
    const updated = localRoleDefinitions.filter(r => r.name !== name);
    setLocalRoleDefinitions(updated);
    
    updateMutation.mutate({ roleDefinitions: updated }, {
      onSuccess: () => toast.success('Role removed'),
      onError: () => toast.error('Failed to remove role')
    });
  };

  const generateInviteLink = () => {
    const baseUrl = `${window.location.origin}/join`;
    const params = new URLSearchParams({
      node: node?.slug || '',
      role: inviteRole
    });
    return `${baseUrl}?${params.toString()}`;
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(generateInviteLink());
    setCopied(true);
    toast.success('Invite link copied');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <div className="flex items-center gap-4 mb-8">
        <Button variant="secondary" size="sm" onClick={() => window.history.back()}>
          Back
        </Button>
        <div>
          <h1 className="text-lg md:text-2xl font-semibold text-gray-900 dark:text-white">{memberTerm}s & Roles</h1>
          <p className="text-xs md:text-sm text-gray-500">Define custom roles and manage organizational structure.</p>
        </div>
      </div>

      {perms.isInheritedAdmin && (
        <div className="p-4 bg-amber-50 border-2 border-black dark:border-white border-amber-200 rounded-none flex items-start gap-3 mb-8 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
          <Shield size={20} className="text-amber-700 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-bold text-amber-900">Managing via Lineage Authority</p>
            <p className="text-xs text-amber-700 leading-relaxed">
              You are managing this organization because you are an admin in a parent organization. 
              This allows you to oversee child nodes, even if they have no local administrators assigned.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Role Definitions */}
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <Shield size={18} className="text-blue-600" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Roles & Capabilities</h3>
          </div>

          <Card className="space-y-6">
            {/* Create Role */}
            {canManageRoles ? (
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Input 
                    placeholder="New Role Name (e.g. Captain)" 
                    value={newRoleName}
                    onChange={(e) => setNewRoleName(e.target.value)}
                  />
                  <Button onClick={handleAddRole} disabled={!newRoleName}>
                    <Plus size={18} />
                    Add
                  </Button>
                </div>
                
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono">Select Capabilities</label>
                  <div className="grid grid-cols-2 gap-2">
                    {ALL_CAPABILITIES.map((cap) => (
                      <label 
                        key={cap.value} 
                        className={cn(
                          "flex items-center gap-2 p-2 rounded-none border-2 border-black dark:border-white text-xs cursor-pointer transition-colors",
                          newRoleCaps.includes(cap.value) 
                            ? "bg-blue-50 border-blue-200 text-blue-700" 
                            : "bg-white border-black dark:border-white text-gray-600 hover:bg-white dark:bg-slate-950"
                        )}
                      >
                        <input 
                          type="checkbox" 
                          className="hidden"
                          checked={newRoleCaps.includes(cap.value)}
                          onChange={() => handleToggleCap(cap.value)}
                        />
                        <div className={cn(
                          "w-3 h-3 rounded-none border-2 border-black dark:border-white flex items-center justify-center",
                          newRoleCaps.includes(cap.value) ? "bg-blue-600 border-blue-600" : "border-black dark:border-white"
                        )}>
                          {newRoleCaps.includes(cap.value) && <Check size={8} className="text-white" />}
                        </div>
                        <span className="truncate">{cap.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-gray-50 border-2 border-black dark:border-white border-dashed text-center text-xs text-gray-500 font-medium dark:bg-slate-950">
                Custom role creation requires ROLE_MANAGE permission.
              </div>
            )}

            <div className="pt-4 space-y-4 border-t border-black dark:border-white">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono">Current Roles</label>
              <div className="space-y-3">
                {roleDefinitions.map((role) => (
                  <div key={role.name} className="p-4 bg-white rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] space-y-3 dark:bg-slate-900">
                    {editingRole === role.name ? (
                      <div className="space-y-4">
                        <Input 
                          value={editRoleName}
                          onChange={(e) => setEditRoleName(e.target.value)}
                        />
                        <div className="grid grid-cols-2 gap-2">
                          {ALL_CAPABILITIES.map((cap) => (
                            <label 
                              key={cap.value} 
                              className={cn(
                                "flex items-center gap-2 p-1.5 rounded-none border-2 border-black dark:border-white text-[10px] cursor-pointer transition-colors",
                                editRoleCaps.includes(cap.value) 
                                  ? "bg-blue-50 border-blue-200 text-blue-700" 
                                  : "bg-white border-black dark:border-white text-gray-600 hover:bg-white dark:bg-slate-950"
                              )}
                            >
                              <input 
                                type="checkbox" 
                                className="hidden"
                                checked={editRoleCaps.includes(cap.value)}
                                onChange={() => handleToggleEditCap(cap.value)}
                              />
                              <div className={cn(
                                "w-2.5 h-2.5 rounded-none border-2 border-black dark:border-white flex items-center justify-center",
                                editRoleCaps.includes(cap.value) ? "bg-blue-600 border-blue-600" : "border-black dark:border-white"
                              )}>
                                {editRoleCaps.includes(cap.value) && <Check size={6} className="text-white" />}
                              </div>
                              {cap.label}
                            </label>
                          ))}
                        </div>
                        <div className="flex gap-2 justify-end">
                          <Button variant="ghost" size="sm" onClick={() => setEditingRole(null)}>Cancel</Button>
                          <Button size="sm" onClick={handleUpdateRole}>Save Changes</Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-gray-900 dark:text-white">{role.name}</span>
                            {role.inherited && <Badge variant="blue" className="text-[10px] uppercase font-mono">Inherited</Badge>}
                            {role.isLocked && <Shield size={12} className="text-gray-400" />}
                          </div>
                          <div className="flex items-center gap-2">
                            {canManageRoles && !role.inherited && (
                              <button 
                                onClick={() => handleEditRole(role)}
                                className="text-gray-400 hover:text-blue-600 transition-colors"
                                title="Edit Role"
                              >
                                <Edit2 size={14} />
                                <span className="sr-only">Edit</span>
                              </button>
                            )}
                            {canManageRoles && !role.isLocked && !role.inherited && (
                              <button 
                                onClick={() => handleRemoveRole(role.name)}
                                className="text-gray-400 hover:text-red-600 transition-colors"
                                title="Remove Role"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {role.capabilities.map(cap => (
                            <span key={cap} className="px-2 py-0.5 bg-white text-gray-600 rounded text-[10px] font-medium border-2 border-black dark:border-white dark:bg-slate-800">
                              {cap.replace(/_/g, ' ')}
                            </span>
                          ))}
                          {role.capabilities.length === 0 && <span className="text-[10px] text-gray-400 italic">No specific capabilities</span>}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>

        {/* Invite Links */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <LinkIcon size={18} className="text-blue-600" />
              <h3 className="font-semibold text-gray-900 dark:text-white">Invite {memberTerm}s</h3>
            </div>
            <div className="flex flex-col items-end min-w-[120px]">
              <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                <Users size={10} className={cn(isRosterFull ? "text-red-500" : "text-gray-400")} />
                <span>{memberCount} / {memberLimit} Total Roster</span>
              </div>
              <div className="w-full h-1">
                <ProgressBar 
                  value={memberPercentage} 
                  variant={isRosterFull ? 'amber' : memberPercentage > 85 ? 'amber' : 'blue'} 
                />
              </div>
            </div>
          </div>

          <Card className="space-y-6">
            {!canInviteMembers ? (
              <div className="p-8 text-center bg-gray-50 border-2 border-black dark:border-white border-dashed rounded-none text-xs text-gray-500 font-medium dark:bg-slate-950">
                {memberTerm} invitations require MEMBER_INVITE permission.
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {isRosterFull && (
                    <div className="p-3 bg-red-50 border-2 border-black dark:border-white border-red-100 rounded-none flex items-center gap-2 text-red-700">
                      <Shield size={16} />
                      <span className="text-[10px] font-bold uppercase tracking-widest">Roster Capacity Reached. Upgrade tier to invite more.</span>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono">Assign Initial Role</label>
                    <select 
                      className="w-full px-3 py-2 text-sm border-2 border-black dark:border-white rounded-none focus:outline-none focus:border-blue-500 disabled:opacity-50"
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value)}
                      disabled={isRosterFull}
                    >
                      {roleDefinitions.map(role => (
                        <option key={role.name} value={role.name}>{role.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-700">Send Direct Email Invite</label>
                    <div className="flex gap-2">
                      <Input 
                        placeholder="member@email.com"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        type="email"
                        disabled={isRosterFull}
                      />
                      <Button 
                        onClick={handleSendInvite} 
                        disabled={!inviteEmail || inviteMutation.isPending || isRosterFull}
                        isLoading={inviteMutation.isPending}
                      >
                        <Send size={16} />
                        Send
                      </Button>
                    </div>
                  </div>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-black dark:border-white"></span>
                    </div>
                    <div className="relative flex justify-center text-[10px] uppercase font-mono tracking-widest text-gray-400">
                      <span className="bg-white px-2 dark:bg-slate-900">Or share magic link</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <div className={cn(
                        "flex-1 px-3 py-2 bg-white border-2 border-black dark:border-white rounded-none text-xs font-mono text-gray-500 truncate dark:bg-slate-950",
                        isRosterFull && "opacity-50"
                      )}>
                        {isRosterFull ? "Invitation link disabled due to capacity" : generateInviteLink()}
                      </div>
                      <Button 
                        variant="secondary" 
                        size="sm" 
                        onClick={copyInviteLink} 
                        disabled={isRosterFull}
                      >
                        {copied ? <Check size={16} className="text-teal-600" /> : <Mail size={16} />}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-blue-50 rounded-none border-2 border-black dark:border-white border-blue-100">
                  <div className="flex gap-3">
                    <Users size={20} className="text-blue-700 flex-shrink-0" />
                    <p className="text-xs text-blue-800 leading-relaxed">
                      Anyone with this link can join your organization with the selected role. You can revoke or change roles at any time from the {memberTerm}s page.
                    </p>
                  </div>
                </div>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};
